import {
  CsvExportDefinition,
  formatDataRecency,
  formatUnixSecondsDate
} from "../../../common/csvExport";
import { getDiagnosticInfo } from "../../../fields/DiagnosticField";
import { getEventCount } from "../../../fields/EventCountField";
import { getTapTestCount } from "../../../fields/TapTestField";

type ProjectPatient = {
  id: string | number;
  codeName?: string;
  appMode?: string;
  realName?: string;
  email?: string;
  createdAt?: number;
  projectStartTime?: number;
  projectEndTime?: number;
  isActive?: boolean;
  [key: string]: unknown;
};

type ProjectPatientExportOptions = {
  patientStartEndDate: boolean;
  projectStart: Date;
  canSeePII?: boolean;
};

const dateOptions: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "numeric"
};
const diagnosticDisplay = (
  info: Awaited<ReturnType<typeof getDiagnosticInfo>>
) =>
  [
    `iOS: ${info.iosVersion}`,
    `/ App: ${info.appVersion}`,
    `/ Mode: ${info.pharmaMode}`,
    info.date ? `(${info.date.toLocaleDateString()})` : "",
    info.mm4pdPermission !== "authorized"
      ? `/ MM4PD Permission: ${info.mm4pdPermission}`
      : ""
  ]
    .filter(Boolean)
    .join(" ");

export const createProjectPatientExportDefinition = (
  options: ProjectPatientExportOptions
): CsvExportDefinition<ProjectPatient> => {
  const recency = (source: string) => (record: ProjectPatient) =>
    formatDataRecency(record[source], {
      isActive: options.patientStartEndDate ? record.isActive : true,
      projectStartTime: options.patientStartEndDate
        ? record.projectStartTime
        : undefined
    });
  const eventColumn = (
    key: string,
    label: string,
    includeFilters: { namespace: string; category: string; enum?: string }[]
  ) => ({
    key,
    source: key,
    label,
    getValue: async (
      record: ProjectPatient,
      context: { dataProvider?: unknown }
    ) => {
      try {
        const count = await getEventCount(
          context.dataProvider,
          record.id,
          includeFilters,
          options.canSeePII
        );
        return count > 0 ? `${count} in 90 days` : "None";
      } catch {
        return "None";
      }
    }
  });

  const columns: CsvExportDefinition<ProjectPatient>["columns"] = [
    {
      key: "codeName",
      source: "codeName",
      label: "Code Name",
      getValue: (record) => record.codeName || ""
    },
    {
      key: "appMode",
      source: "appMode",
      label: "App Mode",
      getValue: (record) => record.appMode || ""
    },
    {
      key: "realName",
      source: "realName",
      label: "Full name",
      getValue: (record) => record.realName || ""
    },
    {
      key: "email",
      source: "email",
      label: "email",
      getValue: (record) => record.email || ""
    },
    {
      key: "createdAt",
      source: "createdAt",
      label: "Onboarded since",
      getValue: (record) => formatUnixSecondsDate(record.createdAt, dateOptions)
    },
    {
      key: "projectStartTime",
      source: "projectStartTime",
      label: "Start Date",
      getValue: (record) =>
        formatUnixSecondsDate(record.projectStartTime, dateOptions)
    },
    {
      key: "projectEndTime",
      source: "projectEndTime",
      label: "End Date",
      getValue: (record) =>
        formatUnixSecondsDate(record.projectEndTime, dateOptions)
    },
    {
      key: "TREMOR_DYSKINESIA",
      source: "TREMOR_DYSKINESIA",
      label: "Tremor & Dyskinesia",
      getValue: recency("TREMOR_DYSKINESIA")
    },
    {
      key: "MOBILITY",
      source: "MOBILITY",
      label: "Mobility",
      getValue: recency("MOBILITY")
    },
    {
      key: "SLEEP",
      source: "SLEEP",
      label: "Sleep",
      getValue: recency("SLEEP")
    },
    {
      key: "TAP_TEST",
      source: "TAP_TEST",
      label: "Tap Test",
      getValue: async (record, context) => {
        const value = recency("TAP_TEST")(record);
        try {
          const count = await getTapTestCount(
            context.dataProvider,
            record.id,
            options.projectStart
          );
          return count > 0 ? `${value} | ${count} in 90 days` : value;
        } catch {
          return value;
        }
      }
    },
    eventColumn("MEDICATION", "Meds", [
      { namespace: "patient", category: "medication", enum: "*" }
    ]),
    eventColumn("BOWEL_MOVEMENT", "BM", [
      {
        namespace: "patient",
        category: "bodily-function",
        enum: "bowel-movement"
      }
    ]),
    {
      key: "diagnostic_info",
      source: "diagnostic_info",
      label: "Diagnostic Info",
      getValue: async (record, context) => {
        try {
          return diagnosticDisplay(
            await getDiagnosticInfo(context.dataProvider, record.id)
          );
        } catch {
          return "";
        }
      }
    }
  ];

  return {
    resource: "ProjectPatient",
    filenameStem: "project-patients",
    id: { header: "patientID", source: "id" },
    columns
  };
};

export type { ProjectPatient, ProjectPatientExportOptions };
